import React, { useState } from 'react'
import { Alert, Box, Stack, ToggleButton, Tooltip, Typography, useTheme } from '@mui/material'
import { toast } from 'react-toastify'
import { useGetProjectMapSVG } from 'src/api/project'
import { useExpandGrid, useGetGridExpansionPreview } from 'src/api/projectVersionPositions'
import { CancelButton } from 'src/components/CancelButton'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MainButton } from 'src/components/MainButton'
import { CenteredSpinner } from 'src/components/Spinner'
import { IPositionType } from 'src/generated-types'
import { IGridExpansionStatus, IGridSide } from 'src/generated-types/grid-expansion'
import { IProjectInfo } from 'src/generated-types/project'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { EXPAND_COUNTS, addedCount, expansionStatusKey, growsColumns } from './expandGrid.action'
import ExpandEdgeLines from './ExpandEdgeLines'
import ExpandGridCanvas from './ExpandGridCanvas'
import { SectionLabel, Segmented, StatTile } from './ExpandGrid.style'
import { refreshProjectData } from './refreshProjectData'
import {
  ActionRow,
  AsideActions,
  AsideHead,
  AsideScroll,
  LegendItem,
  ModeAside,
  ModeCanvas,
  ModeColumn,
  ModeLegend,
  ModeTag,
} from './RevisionModeLayout'
import { useProjectsT } from './useProjectsT'

const SIDES: { side: IGridSide; glyph: string; key: string }[] = [
  { side: IGridSide.Left, glyph: '←', key: 'revision-expand-side-left' },
  { side: IGridSide.Right, glyph: '→', key: 'revision-expand-side-right' },
  { side: IGridSide.Top, glyph: '↑', key: 'revision-expand-side-top' },
  { side: IGridSide.Bottom, glyph: '↓', key: 'revision-expand-side-bottom' },
]

const PLACE_KEY: Record<IGridSide, string> = {
  [IGridSide.Left]: 'revision-expand-place-left',
  [IGridSide.Right]: 'revision-expand-place-right',
  [IGridSide.Bottom]: 'revision-expand-place-bottom',
  [IGridSide.Top]: 'revision-expand-place-top',
}

const STATS: { type: IPositionType; key: string }[] = [
  { type: IPositionType.Corner, key: 'revision-expand-stat-corners' },
  { type: IPositionType.FrameLines, key: 'revision-expand-stat-frame-lines' },
  { type: IPositionType.Cage, key: 'revision-expand-stat-cages' },
  { type: IPositionType.Bridle, key: 'revision-expand-stat-bridles' },
  { type: IPositionType.MooringLine, key: 'revision-expand-stat-mooring-lines' },
]

interface ExpandGridModeProps {
  project: IProjectInfo
  // The revision toolbar, which stays above the preview.
  toolbar: React.ReactNode
  onClose: () => void
}

// Grows the draft's grid on a chosen side, in place of the configure drawing. Existing positions keep their names; new
// ones get the next free names.
const ExpandGridMode: React.FC<ExpandGridModeProps> = ({ project, toolbar, onClose }) => {
  const t = useProjectsT()
  const theme = useTheme()
  const [side, setSide] = useState(IGridSide.Right)
  const [count, setCount] = useState(1)
  const [replaced, setReplaced] = useState<number[]>([])
  const [hovered, setHovered] = useState<number>()
  const { data: svg } = useGetProjectMapSVG(project.customerId, project.localityId, project.id, true)
  const { data: preview, isFetching } = useGetGridExpansionPreview(
    project.customerId,
    project.localityId,
    project.id,
    side,
    count,
  )
  const { mutateAsync: expandGrid, isLoading } = useExpandGrid(project.customerId, project.localityId, project.id)

  const ready = preview?.status === IGridExpansionStatus.Ready
  const statusKey = preview ? expansionStatusKey(preview.status) : undefined
  const columns = growsColumns(side)
  const unit = t(`revision-expand-unit-${columns ? 'column' : 'row'}${count > 1 ? 's' : ''}`)
  const total = preview ? STATS.reduce((sum, stat) => sum + addedCount(preview, stat.type), 0) : 0
  const added = getThemeColor(theme, MUIThemeColors.successMain)
  const removed = getThemeColor(theme, MUIThemeColors.errorMain)
  const muted = getThemeColor(theme, MUIThemeColors.secondaryMain)

  const chooseSide = (value: IGridSide) => {
    setSide(value)
    setReplaced([])
    setHovered(undefined)
  }

  const handleExpand = async () => {
    try {
      await expandGrid({ side, count, replacedLineIds: replaced })
      toast.success(t('revision-expand-done'))
      refreshProjectData(project.id)
      onClose()
    } catch {
      toast.error(t('revision-expand-failed'))
    }
  }

  if (!preview) return <CenteredSpinner />

  return (
    <>
      <ModeColumn>
        {toolbar}
        <ModeCanvas>
          {ready && <ModeTag>{t('revision-expand-preview', { count, unit, place: t(PLACE_KEY[side]) })}</ModeTag>}
          {preview.cages.length > 0 && (
            <ExpandGridCanvas
              svg={svg}
              cages={preview.cages}
              side={side}
              count={ready ? count : 0}
              edgeLines={ready ? preview.edgeLines : []}
              replaced={replaced}
              hoveredId={hovered}
              onSide={chooseSide}
              sideLabel={value => t(SIDES.find(item => item.side === value)?.key ?? '')}
              newLabel={t('revision-expand-canvas-new')}
              movedLabel={name => t('revision-expand-canvas-moved', { name })}
              removedLabel={name => t('revision-expand-canvas-removed', { name })}
            />
          )}
        </ModeCanvas>
        <ModeLegend>
          <span>{t('revision-expand-legend-side')}</span>
          <LegendItem color={muted} dashed>
            {t('revision-expand-legend-added')}
          </LegendItem>
          <LegendItem color={added}>{t('revision-expand-legend-moved')}</LegendItem>
          <LegendItem color={muted}>{t('revision-expand-legend-faded')}</LegendItem>
          <LegendItem color={removed} dashed>
            {t('revision-expand-legend-replaced')}
          </LegendItem>
          <span>{t('revision-expand-legend-hover')}</span>
        </ModeLegend>
      </ModeColumn>
      <ModeAside>
        <AsideHead>
          <GridActionButton
            border={0}
            shadowWeight={null}
            buttonText={`← ${t('revision-mode-back')}`}
            buttonTextProps={{ fontSize: 12 }}
            onClick={onClose}
            sx={{ alignSelf: 'flex-start', padding: 0, minWidth: 0 }}
          />
          <Typography variant="h6" fontWeight={600}>
            {t('revision-expand-grid')}
          </Typography>
          <Typography fontSize={12} color="text.secondary">
            {t('revision-expand-description')}
          </Typography>
        </AsideHead>
        <AsideScroll>
          <Stack direction="row" flexWrap="wrap" gap={2.5}>
            <Stack spacing={1}>
              <SectionLabel>{t('revision-expand-side-title')}</SectionLabel>
              <Segmented
                size="small"
                exclusive
                value={side}
                onChange={(_, value: IGridSide | null) => value != null && chooseSide(value)}
              >
                {SIDES.map(item => (
                  <Tooltip key={item.side} title={t(item.key)}>
                    <ToggleButton value={item.side} aria-label={t(item.key)}>
                      {item.glyph}
                    </ToggleButton>
                  </Tooltip>
                ))}
              </Segmented>
            </Stack>
            <Stack spacing={1}>
              <SectionLabel>{t(columns ? 'revision-expand-columns' : 'revision-expand-rows')}</SectionLabel>
              <Segmented
                size="small"
                exclusive
                value={count}
                onChange={(_, value: number | null) => value && setCount(value)}
              >
                {EXPAND_COUNTS.map(option => (
                  <ToggleButton key={option} value={option}>
                    {option}
                  </ToggleButton>
                ))}
              </Segmented>
            </Stack>
          </Stack>
          {ready && (
            <Stack spacing={1}>
              <SectionLabel>{t('revision-expand-will-add')}</SectionLabel>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {STATS.filter(stat => addedCount(preview, stat.type) > 0).map(stat => (
                  <StatTile key={stat.type}>
                    <b>{addedCount(preview, stat.type)}</b>
                    <span>{t(stat.key)}</span>
                  </StatTile>
                ))}
              </Box>
            </Stack>
          )}
          {statusKey && <Alert severity="info">{t(statusKey)}</Alert>}
          {ready && preview.edgeLines.length > 0 && (
            <ExpandEdgeLines
              lines={preview.edgeLines}
              replaced={replaced}
              onReplacedChange={setReplaced}
              onHover={setHovered}
              t={t}
            />
          )}
        </AsideScroll>
        <AsideActions>
          {ready && (
            <Typography fontSize={12} color="text.secondary">
              {t('revision-expand-adds-total', { count: total })}
            </Typography>
          )}
          <ActionRow>
            <CancelButton onClick={onClose} sx={{ flex: 'none' }} />
            <MainButton sx={{ flex: 1 }} loading={isLoading} disabled={!ready || isFetching} onClick={handleExpand}>
              {t('revision-expand-apply')}
            </MainButton>
          </ActionRow>
        </AsideActions>
      </ModeAside>
    </>
  )
}

export default ExpandGridMode
